<script setup lang="ts">
import type { VSwitchRowProps } from "./VSwitchRow.types";
import { computed, inject } from "vue";
import VHelpTooltip from "../HelpTooltip/VHelpTooltip.vue";
import VSwitch from "../Switch/VSwitch.vue";
import { useFieldId } from "../useFieldId";
import { switchRowListCva } from "./switchRow.cva";
import { SWITCH_ROW_LIST_KEY, SWITCH_ROW_TALL_KEY } from "./VSwitchRow.types";

defineOptions({
  name: "VSwitchRow",
});

/**
 * A setting with a switch: the title (with an optional help question mark) and the option's own description on the left,
 * the switch on the right.
 */
const {
  title,
  help = "",
  description = "",
  disabled = false,
  disabledReason = undefined,
  loading = false,
  emphasis = "default",
  card = false,
  indent = false,
  compact = false,
  id: rawId = undefined,
  testId = undefined,
} = defineProps<VSwitchRowProps>();

defineSlots<{
  /** Content between the text and the switch, e.g. a link button to the partner. */
  actions?: () => unknown
}>();

const modelValue = defineModel<boolean>({ default: false });

const inList = inject(SWITCH_ROW_LIST_KEY, false);
const tall = inject(SWITCH_ROW_TALL_KEY, false);
const id = useFieldId(() => rawId, { inheritFieldId: false });
const descriptionId = computed(() => `${id.value}-description`);

const titleClass = computed(() => (emphasis === "strong" ? "text-[15px] leading-5" : "text-sm"));
const isDisabled = computed(() => disabled || Boolean(disabledReason));

/**
 * The whole row is the click target of the switch: the title label is stretched over the row (see `labelClass`), so a
 * click anywhere on it toggles the switch while the help button, the actions and the switch itself stay above it.
 */
const rowClass = computed(() => {
  const hover = isDisabled.value ? null : "transition-colors hover:bg-gray-50 dark:hover:bg-dark-700/60";
  if (inList) {
    return [switchRowListCva({ tall, indent }), hover];
  }
  if (card) {
    return ["rounded-xl border border-gray-300 px-3.5 py-3 dark:border-dark-600", hover];
  }
  return [compact ? "rounded-md px-2 py-1.5" : "-mx-2 rounded-lg px-2 py-1", hover];
});

const labelClass = computed(() => [
  "select-none after:absolute after:inset-0 after:content-['']",
  compact && !inList ? "text-sm text-gray-700 dark:text-gray-200" : ["font-medium text-(--text)", titleClass.value],
  isDisabled.value ? "cursor-not-allowed" : "cursor-pointer",
]);
</script>

<template>
  <div
    class="relative flex items-center gap-4"
    :class="rowClass"
    :data-row-indent="inList && indent ? '' : undefined"
    :data-testid="testId"
  >
    <div class="flex min-w-0 flex-1 flex-col gap-[3px]">
      <div class="flex min-w-0 items-center gap-[5px]">
        <label :for="id" :class="labelClass">
          {{ title }}
        </label>
        <VHelpTooltip v-if="help" class="relative z-[1]" :content="help" :subject="title" />
      </div>
      <p v-if="description" :id="descriptionId" class="m-0 text-[13px] leading-[1.45] text-gray-700 dark:text-gray-400">
        {{ description }}
      </p>
    </div>
    <div v-if="$slots.actions" class="relative z-[1] flex shrink-0 items-center">
      <slot name="actions" />
    </div>
    <VSwitch
      :id="id"
      v-model="modelValue"
      class="relative z-[1] shrink-0"
      :size="compact && !inList ? 'small' : 'medium'"
      :disabled="disabled"
      :disabled-reason="disabledReason"
      :loading="loading"
      :aria-describedby="description ? descriptionId : undefined"
    />
  </div>
</template>
